import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { checkMembershipExpiry, getMembership } from "../api/membershipAPI";
import {
  selectMembershipDetails,
  setMembership,
} from "../store/slices/membershipSlice";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { getAthletesByMember } from "../api/athletesAPI";
import LoadingSpinner from "../components/common/LoadingSpinner";
import PrivateAthleteAttendance from "../components/athlete/attendance/PrivateAthleteAttendance";
import MemberBillingHistory from "../components/membership/BillingHistory/MemberBillingHistory";
import { getStripePaymentMethods } from "../api/paymentsAPI";
import { getNewsBroadcasts } from "../api/emailAPI";
import { captureError } from "../utils/sentry";
import { useTheme } from "../context/ThemeContext";
import { getPrimaryText, getSecondaryText } from "../utils/themeUtils";
import { selectSettings } from "../store/slices/settingsSlice";
import { PageContainer, PageHeading, Card, SectionHeading, Button } from "../components/ui";
import { buttonClasses } from "../utils/designTokens";
import OnboardingWalkthrough, { OnboardingStep } from "../components/common/OnboardingWalkthrough";
import { useOnboarding } from "../hooks/useOnboarding";
import DOMPurify from "isomorphic-dompurify";
import { Newspaper } from "lucide-react";
import MemberScheduleCalendar from "../components/membership/MemberScheduleCalendar";
import DashboardProgressWidget from "../components/monitoring/DashboardProgressWidget";
import MemberCoursesWidget from "../components/lms/MemberCoursesWidget";
import MyVolunteerSignupsCard from "../components/events/MyVolunteerSignupsCard";
import { persistAuthToken } from "../api/authTokenStore";
import AddSecondParentPrompt from "../components/membership/AddSecondParentPrompt";
import MedicalInfoPrompt from "../components/membership/MedicalInfoPrompt";

const Dashboard: React.FC = () => {
  const { t } = useTranslation(['dashboard', 'common', 'onboarding']);
  // STATES
  const [, setIsExpired] = useState<boolean>(false); // To store membership status
  const [athletes, setAthletes] = useState<any[]>([]); // State to store staff data
  const [loading, setLoading] = useState<boolean>(true); // Loading state
  const [error, setError] = useState<string | null>(null); // Error state
  const [news, setNews] = useState<any[]>([]);
  const [expandedNewsId, setExpandedNewsId] = useState<string | null>(null);
  // THEME
  const { isDarkMode } = useTheme();
  // HELPER
  const token = localStorage.getItem("authToken");
  const dispatch = useDispatch();
  const membership: any = useSelector(selectMembershipDetails);
  const setting: any = useSelector(selectSettings);
  const { pathname } = useLocation();
  const tenantSlug = pathname.split("/").filter(Boolean)[0] || "";

  // "Display notice to Member" (Negative Balance settings): a member whose
  // balance has passed the club's Maximum Negative Balance sees it here. The
  // same line as the staff attendance notice and the balance email
  // (max_negative_balance is stored negative; 0 means any amount owed).
  const balance = Number(membership?.balance);
  const maxNegative = Number(setting?.max_negative_balance) || 0;
  const showBalanceNotice =
    !!setting?.notify_member_notice && Number.isFinite(balance) && balance < 0 && balance < maxNegative;
  const memoizedAthletes = useMemo(() => [...athletes], [athletes]);
  // HELPER

  useRef(false);
  useRef(false);
  useRef(false);
  useRef(false);
  useRef(false);
  useRef(false);

  // Onboarding walkthrough steps
  const onboardingSteps: OnboardingStep[] = [
    {
      target: '[data-onboarding="page-title"]',
      title: t('onboarding:dashboard.welcome.title'),
      content: t('onboarding:dashboard.welcome.content'),
      placement: 'bottom',
      highlightPadding: 12,
    },
    {
      target: '[data-onboarding="attendance-section"]',
      title: t('onboarding:dashboard.attendance.title'),
      content: t('onboarding:dashboard.attendance.content'),
      placement: 'bottom',
      highlightPadding: 8,
    },
    {
      target: '[data-onboarding="billing-section"]',
      title: t('onboarding:dashboard.billing.title'),
      content: t('onboarding:dashboard.billing.content'),
      placement: 'bottom',
      highlightPadding: 8,
    },
  ];

  const {
    shouldShow,
    isActive,
    markAsCompleted,
    markAsDismissed,
    startWalkthrough,
  } = useOnboarding({
    storageKey: 'onboarding-dashboard',
    autoStart: false,
  });

  // Required membership fields
  const REQUIRED_MEMBER_FIELDS = [
    "first_name",
    "last_name",
    "email",
    "phone",
    "address",
    "city",
    "state",
    "zip",
    "alternate_email",
    "alternate_email_2",
    "mobile_sms",
    "carrier",
    "referral",
    "alternate_phone",
  ];


  const checkMembershipStatus = async () => {
    try {
      const response = await checkMembershipExpiry(token);

      if (response?.status === 200) {
        setIsExpired(response?.data?.expired);

        // Toast notifications temporarily disabled
        // const dismissedExpiry = localStorage.getItem('dismissedExpiryToast');
        // if (expired && !expiryToastTriggered.current && !dismissedExpiry) {
        //   toast.error(t('dashboard:notifications.membershipExpired'), {
        //     onClose: () => {
        //       localStorage.setItem('dismissedExpiryToast', 'true');
        //     }
        //   });
        //   expiryToastTriggered.current = true;
        // }
      } else if (response) {
        // Got an actual response with non-200 status
        captureError(new Error("Error checking expiry: " + JSON.stringify(response?.data)), { component: "Dashboard", operation: "checkExpiry" }, "warning");
        setIsExpired(false);
      }
      // NOTE: there used to be a third branch here for `response === undefined`,
      // which was how a failed fetch arrived before checkMembershipExpiry
      // rethrew. It is unreachable now — a failure lands in the catch below,
      // which reaches the same setIsExpired(false) AND reports to Sentry.
    } catch (error) {
      captureError(error, { component: "Dashboard", operation: "checkExpiry" }, "warning");
      setIsExpired(false);
    }
  };

  const checkProfileCompletion = (memberData: any) => {
    if (!memberData) return;

    // Check missing membership fields
    REQUIRED_MEMBER_FIELDS?.filter(
      (field) => !memberData[field] || String(memberData[field])?.trim() === ""
    );

    // Toast notifications temporarily disabled
    // const dismissedMemberProfile = localStorage.getItem('dismissedMemberProfileToast');
    // if (missingMemberFields?.length > 0 && !memberToastTriggered.current && !dismissedMemberProfile) {
    //   toast.warning(
    //     t('dashboard:notifications.completeMemberProfile'),
    //     {
    //       onClose: () => {
    //         localStorage.setItem('dismissedMemberProfileToast', 'true');
    //       }
    //     }
    //   );
    //   memberToastTriggered.current = true;
    // }

    localStorage.getItem('dismissedAthleteProfileToast');
    localStorage.getItem('dismissedBalanceToast');

    // Toast notifications temporarily disabled
    // if (hasMissingAthleteFields && !athleteToastTriggered.current && !dismissedAthleteProfile) {
    //   toast.warning(
    //     t('dashboard:notifications.completeAthleteProfile'),
    //     {
    //       onClose: () => {
    //         localStorage.setItem('dismissedAthleteProfileToast', 'true');
    //       }
    //     }
    //   );
    //   athleteToastTriggered.current = true;
    // }

    // The past-due balance notice is shown on the page (showBalanceNotice),
    // not as a toast.
  };

  // ✅ Check if default payment method exists
  const checkPaymentMethod = async (stripeCustomerId: string) => {
    try {
      if (!stripeCustomerId) return;
      const response = await getStripePaymentMethods(token, stripeCustomerId);
      if (response?.success) {
        // Toast notifications temporarily disabled
        // const dismissedPayment = localStorage.getItem('dismissedPaymentMethodToast');
        // if (
        //   (!Array.isArray(savedCards) || savedCards?.length === 0) &&
        //   !paymentToastTriggered.current &&
        //   !dismissedPayment
        // ) {
        //   toast.warning(
        //     <span>
        //       {t('dashboard:notifications.addPaymentMethod')}{" "}
        //       <Link
        //         to={`/${xTenantId}/member/update-card`}
        //         style={{ color: "#2563eb", textDecoration: "underline" }}
        //       >
        //         {t('dashboard:notifications.clickHere')}
        //       </Link>
        //     </span>,
        //     {
        //       autoClose: false,
        //       onClose: () => {
        //         localStorage.setItem('dismissedPaymentMethodToast', 'true');
        //       }
        //     }
        //   );
        //   paymentToastTriggered.current = true;
        // }
      }
    } catch (error) {
      // Handled by axios interceptor
    }
  };

  useEffect(() => {
    checkMembershipStatus();
  }, []);

  useEffect(() => {
    if (membership) {
      checkProfileCompletion(membership);

      // ✅ Check membership status for inactive/archived
      // Toast notifications temporarily disabled
      // const dismissedInactive = localStorage.getItem('dismissedInactiveToast');
      // if (
      //   (membership?.status === "inactive" ||
      //     membership?.status === "archived") &&
      //   !inactiveToastTriggered.current &&
      //   !dismissedInactive
      // ) {
      //   toast.warning(
      //     <span>
      //       {t('dashboard:notifications.accountInactive', { status: membership.status })}{" "}
      //       <Link
      //         to={`/${xTenantId}/member/manage-profile`}
      //         style={{ color: "#2563eb", textDecoration: "underline" }}
      //       >
      //         {t('dashboard:notifications.reactivateNow')}
      //       </Link>
      //     </span>,
      //     {
      //       autoClose: false,
      //       onClose: () => {
      //         localStorage.setItem('dismissedInactiveToast', 'true');
      //       }
      //     }
      //   );
      //   inactiveToastTriggered.current = true;
      // }
    }
  }, [membership]); // Check profile completion

  // ... after membership is loaded
  useEffect(() => {
    if (membership?.stripe_customer_id) {
      checkPaymentMethod(membership?.stripe_customer_id);
    }
  }, [membership?.stripe_customer_id]);

  useEffect(() => {
    // Fetch membership details first
    const fetchMembershipDetails = async () => {
      try {
        const response = await getMembership(token);

        // Assuming the membership details have a field `id`
        if (response && response.data) {
          dispatch(setMembership(response?.data?.data));
        }
      } catch (err) {
        // Handled by axios interceptor
      }
    };

    if (token) {
      fetchMembershipDetails();
    }

    // Refetch membership when user returns to tab (handles stale balance on mobile)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && token) {
        fetchMembershipDetails();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []); // Fetch member

  useEffect(() => {
    const fetchAthletes = async () => {
      if (membership && membership?.id) {
        try {
          const response = await getAthletesByMember(token, membership?.id);
          if (response?.status === "success") {
            setAthletes(response?.data);
            persistAuthToken(response?.token);
          }
        } catch (error: any) {
          setError(error.message);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchAthletes();
  }, [membership]); // Fetch athletes by member

  // Fetch news broadcasts
  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await getNewsBroadcasts(token);
        if (res?.success && res?.data) {
          setNews(res.data);
        }
      } catch (err) {
        // Handled by axios interceptor
      }
    };
    if (token) {
      fetchNews();
    }
  }, [token]);

  // Start onboarding after data loads
  useEffect(() => {
    if (!loading && shouldShow && !isActive) {
      const timer = setTimeout(() => {
        startWalkthrough();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, shouldShow, isActive, startWalkthrough]);

  if (loading) {
    return (
      <div
        className="w-full flex justify-center items-center"
        style={{ height: "calc(100vh - 110px)" }}
      >
        <LoadingSpinner size="large" color="blue" />
      </div>
    );
  }

  if (error) {
    return <div>{t('common:messages.error')}: {error}</div>;
  }

  return (
    <PageContainer>
        <PageHeading
          title={t('dashboard:title')}
          actions={
            /* Families had no way to the Club Central help articles (2026-10-11). */
            <Link
              to={`/${tenantSlug}/member/help`}
              className="text-sm font-medium underline text-[var(--cc-link)]"
            >
              {t('common:nav.help', { defaultValue: 'Help' })}
            </Link>
          }
        />

        {showBalanceNotice && (
          <div
            role="status"
            aria-label="Past due balance"
            className={`mb-4 sm:mb-6 rounded-lg border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
              isDarkMode ? "border-red-700 bg-red-900/30 text-red-200" : "border-red-200 bg-red-50 text-red-800"
            }`}
          >
            <p className="text-sm font-medium">
              {t("dashboard:notifications.pastDueNotice", { amount: Math.abs(balance).toFixed(2) })}
            </p>
            {/* A button, not an underlined link: this is the one thing the
                notice asks the family to do (PM f744a768, banner blindness). */}
            <Link
              to={`/${tenantSlug}/member/view-billing`}
              className={`${buttonClasses("danger", "md")} whitespace-nowrap self-start sm:self-auto`}
            >
              {t("dashboard:notifications.payNow")}
            </Link>
          </div>
        )}

        {/* Nudge to add a second parent/guardian — members only, dismissible,
            auto-hides once the account has any additional contact. */}
        <AddSecondParentPrompt membership={membership} />

        {/* Nudge to add medical information (or confirm there is none) for
            each athlete with nothing on file — members only, "Later" hides
            it, and either answer stops it for good. */}
        <MedicalInfoPrompt membership={membership} />

        {/* My volunteer signups — auto-hides when the user has none */}
        <MyVolunteerSignupsCard authToken={localStorage.getItem("authToken")} />

        {/* News Section */}
        {news.length > 0 && (
          <Card padding="md" className="mb-4 sm:mb-6 lg:mb-8">
            <SectionHeading icon={<Newspaper className="w-5 h-5" />}>News</SectionHeading>
            <div className="space-y-3">
              {news.map((item: any) => (
                <div
                  key={item.id}
                  className={`p-4 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-medium ${getPrimaryText(isDarkMode)}`}>{item.subject}</h3>
                      <p className={`text-xs mt-1 ${getSecondaryText(isDarkMode)}`}>
                        {item.sent_by_name && `By ${item.sent_by_name} · `}
                        {new Date(item.sent_at || item.created_at).toLocaleDateString(undefined, {
                          year: 'numeric', month: 'short', day: 'numeric',
                        })}
                      </p>
                    </div>
                    {item.image_url && (
                      <img
                        src={item.image_url}
                        alt=""
                        className="w-16 h-16 rounded object-cover flex-shrink-0"
                      />
                    )}
                  </div>
                  {expandedNewsId === item.id ? (
                    <div className="mt-3">
                      <div
                        className={`prose prose-sm max-w-none ${isDarkMode ? 'prose-invert' : ''}`}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.message || item.plain_text || '') }}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedNewsId(null)}
                        className="mt-2"
                      >
                        Show less
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <p className={`text-sm ${getSecondaryText(isDarkMode)} line-clamp-2`}>
                        {item.plain_text
                          ? item.plain_text.replace(/<[^>]*>/g, '').substring(0, 150)
                          : ''}
                        {(item.plain_text || '').length > 150 ? '...' : ''}
                      </p>
                      {(item.message || item.plain_text || '').length > 150 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedNewsId(item.id)}
                          className="mt-1"
                        >
                          Read more
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Schedule Calendar */}
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <MemberScheduleCalendar athletes={memoizedAthletes} />
        </div>

        {/* Athlete Progress */}
        {memoizedAthletes.length > 0 && (
          <div className="mb-4 sm:mb-6 lg:mb-8">
            <DashboardProgressWidget athletes={memoizedAthletes} />
          </div>
        )}

        {/* LMS Courses Widget */}
        {setting?.lms_enabled && memoizedAthletes.length > 0 && (
          <div className="mb-4 sm:mb-6 lg:mb-8">
            <MemberCoursesWidget athleteIds={memoizedAthletes.map((a: any) => a.id)} />
          </div>
        )}

        <div className="flex flex-col lg:flex-row lg:justify-between gap-4 sm:gap-5 lg:gap-6">
          {/* Athletes Attendance Section - Full width on mobile, 39% on desktop */}
          <Card
            data-onboarding="attendance-section"
            padding="md"
            className="w-full lg:w-[39%] overflow-y-auto"
          >
            <PrivateAthleteAttendance athletes={memoizedAthletes} />
          </Card>

          {/* Billing History Section - Full width on mobile, 59% on desktop */}
          <Card
            data-onboarding="billing-section"
            padding="md"
            className="w-full lg:w-[59%] overflow-y-auto"
          >
            <MemberBillingHistory
              widthClass="w-full lg:w-[80%]"
              source={"dashboard"}
            />
          </Card>
        </div>

        {/* Onboarding Walkthrough */}
        {shouldShow && (
          <OnboardingWalkthrough
            steps={onboardingSteps}
            storageKey="onboarding-dashboard"
            onComplete={markAsCompleted}
            onDismiss={markAsDismissed}
            isActive={isActive}
          />
        )}
    </PageContainer>
  );
};

export default Dashboard;
